import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import { isOwner } from "../../common/validators";
import {
  getMySubscription,
  getSubscriptionPlans,
  upgradeSubscription,
  createStripeCheckoutSession,
  createStripePortalSession,
} from "../../api/subscription";

import CurrentPlanCard from "./CurrentPlanCard";
import FeatureAccessGrid from "./FeatureAccessGrid";
import UsageBars from "./UsageBars";
import AddonsSection from "./AddonsSection";
import PlansComparison from "./PlansComparison";
import UpgradeConfirmDialog from "./UpgradeConfirmDialog";

export default function MySubscriptionPage() {
  const history = useHistory();
  const userType = useSelector((state) => state.auth.user?.user_type);

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upgrade dialog
  const [upgradePlan, setUpgradePlan] = useState(null);
  const [upgradeCycle, setUpgradeCycle] = useState("monthly");
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);

  useEffect(() => {
    if (!isOwner(userType)) {
      history.replace("/central-delivery");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [subRes, plansRes] = await Promise.all([
          getMySubscription(),
          getSubscriptionPlans(),
        ]);
        setSubscription(subRes.data);
        setPlans(plansRes.data);
      } catch (err) {
        setError("Erro ao carregar dados da assinatura.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userType, history]);

  const handleUpgradeClick = (plan, cycle) => {
    setUpgradePlan(plan);
    setUpgradeCycle(cycle);
  };

  const handleUpgradeConfirm = async (planId, cycle) => {
    try {
      setUpgradeLoading(true);
      const selectedPlan = plans.find((p) => p.plan_id === planId);
      const isPaid = Number(selectedPlan?.price_monthly) > 0;

      if (isPaid) {
        const res = await createStripeCheckoutSession({
          plan_id: planId,
          billing_cycle: cycle,
        });
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        throw new Error("Checkout URL não retornada.");
      }

      const res = await upgradeSubscription({
        plan_id: planId,
        billing_cycle: cycle,
      });
      setSubscription(res.data);
      setUpgradePlan(null);
      setUpgradeSuccess(true);
      setTimeout(() => setUpgradeSuccess(false), 5000);
    } catch (err) {
      const detail =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.response?.data?.plan_id?.[0] ||
        "Erro ao mudar plano.";
      setError(detail);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleRenew = async () => {
    try {
      setError(null);
      const planId = subscription?.plan_details?.plan_id;
      const cycle = subscription?.billing_cycle || "monthly";
      if (!planId) return;
      const res = await createStripeCheckoutSession({
        plan_id: planId,
        billing_cycle: cycle,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      throw new Error("Checkout URL não retornada.");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          "Não foi possível iniciar o pagamento.",
      );
    }
  };

  const handleOpenPortal = async () => {
    try {
      const res = await createStripePortalSession();
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Não foi possível abrir o portal de gerenciamento.",
      );
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const usageStats = subscription?.usage_stats;
  const features = usageStats?.features;
  const usage = usageStats?.usage;
  const currentPlanId = subscription?.plan_details?.plan_id;
  const subscriptionStatus = subscription?.status;
  const currentPlanIsPaid =
    Number(subscription?.plan_details?.price_monthly) > 0;
  // `status` lags reality: it only flips to grace_period/expired when the daily
  // `check_expired_subscriptions` cron runs, so a subscription past its
  // `expires_at` can still read as "active" for hours. The backend already
  // resolves this in UserSubscription.is_active() (exposed as is_active_status),
  // which returns False for an active-but-past-expires_at row. Without this the
  // user is left on a disabled "Plano Atual" button with no way to pay.
  const isLapsed = subscription?.is_active_status === false;
  const needsRenewal =
    currentPlanIsPaid &&
    (isLapsed ||
      ["grace_period", "expired", "suspended"].includes(subscriptionStatus));

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 4,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Minha Assinatura
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {upgradeSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setUpgradeSuccess(false)}>
          Plano atualizado com sucesso!
        </Alert>
      )}

      {needsRenewal && (
        <Alert
          severity="warning"
          sx={{ mb: 3, alignItems: "center" }}
          action={
            <Button
              color="warning"
              variant="contained"
              size="small"
              startIcon={<CreditCardIcon />}
              onClick={handleRenew}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Renovar agora
            </Button>
          }
        >
          {subscriptionStatus === "grace_period"
            ? "Sua assinatura está em período de carência. Regularize o pagamento para não perder o acesso."
            : "Sua assinatura está vencida. Realize o pagamento para reativar o acesso."}
        </Alert>
      )}

      <Stack spacing={4}>
        <CurrentPlanCard subscription={subscription} />

        {subscription?.stripe_customer_id && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<CreditCardIcon />}
              onClick={handleOpenPortal}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Gerenciar pagamento e assinatura
            </Button>
            {subscription?.cancel_at_period_end && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Sua assinatura será cancelada ao final do período atual.
              </Alert>
            )}
          </Box>
        )}

        <FeatureAccessGrid features={features} />
        <UsageBars usage={usage} />
        <AddonsSection />
        <PlansComparison
          plans={plans}
          currentPlanId={currentPlanId}
          subscriptionNeedsRenewal={needsRenewal}
          onUpgrade={handleUpgradeClick}
        />
      </Stack>

      <UpgradeConfirmDialog
        open={Boolean(upgradePlan)}
        plan={upgradePlan}
        initialCycle={upgradeCycle}
        onClose={() => setUpgradePlan(null)}
        onConfirm={handleUpgradeConfirm}
        loading={upgradeLoading}
      />
    </Box>
  );
}
